<template>
	<v-dialog
		v-model="dialog"
		:max-width="fullscreen ? undefined : '900px'"
		:fullscreen="fullscreen || $vuetify.breakpoint.smAndDown"
		:hide-overlay="fullscreen"
		transition="dialog-bottom-transition">
		<v-card :class="fullscreen ? 'background-2' : 'background-2 round-card'" :tile="fullscreen">
			<v-card-title class="pa-3">
				<v-icon left :color="fileColor">{{ fileIcon }}</v-icon>
				<!-- Le frecce + contatore mangiano spazio in testata: il titolo si
				     stringe solo quando ci sono. Senza modalita' lista resta il 70%
				     di sempre, cosi' i consumer esistenti non troncano prima. -->
				<p class="pl-2 pl-xl-3 text-truncate" :style="{ 'max-width': hasNav ? '60%' : '70%' }">{{ displayName }}</p>
				<v-spacer></v-spacer>
				<template v-if="hasNav">
					<v-btn icon :disabled="index === 0" @click="go(index - 1)">
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
					<span class="body-2 mx-1">{{ index + 1 }} / {{ items.length }}</span>
					<v-btn icon :disabled="index >= items.length - 1" @click="go(index + 1)">
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
				</template>
				<v-btn icon @click="download">
					<v-icon>mdi-download</v-icon>
				</v-btn>
				<v-btn icon @click="fullscreen = !fullscreen">
					<v-icon>{{ fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
				</v-btn>
				<v-btn icon @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-divider></v-divider>
			<v-card-text class="pa-0" :style="{ minHeight: fullscreen ? 'calc(100vh - 65px)' : '400px', maxHeight: fullscreen ? 'calc(100vh - 65px)' : '700px', overflow: 'auto', overscrollBehavior: 'contain' }">
				<div class="d-flex align-center justify-center" :style="{ minHeight: fullscreen ? 'calc(100vh - 65px)' : '400px' }">

					<!-- Loading -->
					<v-progress-circular
						v-if="loading"
						indeterminate
						color="primary"
					></v-progress-circular>

					<!-- Immagini -->
					<v-img
						v-else-if="isImage && blobUrl"
						:src="blobUrl"
						contain
						:max-height="fullscreen ? undefined : 700"
						class="ma-4"
						:style="fullscreen ? 'max-height: calc(100vh - 65px)' : ''"
					></v-img>

					<!-- Video -->
					<video
						v-else-if="isVideo && blobUrl"
						:src="blobUrl"
						controls
						:style="'width: 100%; max-height: ' + (fullscreen ? 'calc(100vh - 65px)' : '700px')"
					></video>

					<!-- PDF -->
					<iframe
						v-else-if="isPdf && blobUrl"
						:src="blobUrl"
						:style="'width: 100%; border: none; height: ' + (fullscreen ? 'calc(100vh - 65px)' : '700px')"
					></iframe>

					<!-- Fallback -->
					<div v-else-if="!loading" class="text-center pa-8">
						<v-icon size="64" color="grey">mdi-file</v-icon>
						<div class="text-body-1 grey--text mt-4">Anteprima non disponibile</div>
						<v-btn color="primary" class="mt-4" @click="download">
							<v-icon left>mdi-download</v-icon>Scarica
						</v-btn>
					</div>
				</div>
			</v-card-text>
			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text color="primary" @click="close">Chiudi</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
export default {
	name: 'FilePreviewDialog',
	props: {
		value: { type: Boolean, default: false },
		doc: { type: Object, default: null },
		url: { type: String, default: '' },
		// Modalita' lista (opzionale): [{doc, url}] → frecce e contatore in testata,
		// doc/url singoli ignorati. I consumer esistenti non passano nulla e
		// restano identici.
		items: { type: Array, default: null },
		startIndex: { type: Number, default: 0 }
	},
	data() {
		return {
			fullscreen: false,
			loading: false,
			blobUrl: null,
			index: 0
		}
	},
	watch: {
		value(val) {
			if (val) {
				this.index = this.hasItems ? Math.min(this.startIndex, this.items.length - 1) : 0
				if (this.activeUrl) this.fetchBlob()
			}
			if (!val) {
				this.fullscreen = false
				this.revokeBlob()
			}
		}
	},
	computed: {
		dialog: {
			get() { return this.value },
			set(val) { this.$emit('input', val) }
		},
		hasItems() {
			return Array.isArray(this.items) && this.items.length > 0
		},
		hasNav() {
			return this.hasItems && this.items.length > 1
		},
		activeDoc() {
			return this.hasItems ? (this.items[this.index] && this.items[this.index].doc) : this.doc
		},
		activeUrl() {
			return this.hasItems ? (this.items[this.index] && this.items[this.index].url) : this.url
		},
		ext() {
			return this.activeDoc ? (this.activeDoc.ext || '').toLowerCase() : ''
		},
		displayName() {
			if (!this.activeDoc) return ''
			var name = this.activeDoc.name || ''
			return name.endsWith('.' + this.ext) ? name : name + '.' + this.ext
		},
		isImage() {
			return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(this.ext)
		},
		isVideo() {
			return ['mp4', 'webm'].includes(this.ext)
		},
		isPdf() {
			return this.ext === 'pdf'
		},
		fileIcon() {
			var map = {
				pdf: 'mdi-file-pdf-box', jpg: 'mdi-file-image', jpeg: 'mdi-file-image',
				png: 'mdi-file-image', gif: 'mdi-file-image', webp: 'mdi-file-image',
				mp4: 'mdi-file-video', webm: 'mdi-file-video'
			}
			return map[this.ext] || 'mdi-file'
		},
		fileColor() {
			var map = {
				pdf: 'red', jpg: 'purple', jpeg: 'purple', png: 'purple',
				gif: 'purple', webp: 'purple', mp4: 'blue', webm: 'blue'
			}
			return map[this.ext] || 'grey'
		}
	},
	methods: {
		go(nextIndex) {
			if (!this.hasItems || nextIndex < 0 || nextIndex >= this.items.length) return
			this.index = nextIndex
			this.revokeBlob()
			if (this.activeUrl) this.fetchBlob()
		},

		async fetchBlob() {
			this.loading = true
			try {
				var encodedUrl = this.activeUrl.split('/').map(segment => encodeURIComponent(segment)).join('/')
				var resp = await this.axios.get(encodedUrl, {
					responseType: 'blob',
					headers: { Accept: '*/*' }
				})
				this.blobUrl = window.URL.createObjectURL(resp.data)
			} catch (e) {
				this.dialog = false
			} finally {
				this.loading = false
			}
		},
		revokeBlob() {
			if (this.blobUrl) {
				window.URL.revokeObjectURL(this.blobUrl)
				this.blobUrl = null
			}
		},
		download() {
			if (this.blobUrl) {
				var link = document.createElement('a')
				link.href = this.blobUrl
				link.download = this.displayName
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
			}
		},
		close() {
			this.$emit('input', false)
		}
	},
	beforeDestroy() {
		this.revokeBlob()
	}
}
</script>
